import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, Eye, EyeOff, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('sales_rep');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState('phone');
  const [forgotPhone, setForgotPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const validatePhone = (phoneNum) => {
    const cleaned = phoneNum.replace(/\s/g, '');
    const phoneRegex = /^(\+91)?[6-9]\d{9}$/;
    return phoneRegex.test(cleaned);
  };

  const formatPhone = (phoneNum) => {
    let cleaned = phoneNum.replace(/\s/g, '').replace(/^(\+91)?/, '');
    return '+91' + cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validatePhone(phone)) {
      toast.error('Invalid phone number. Please enter a valid 10-digit Indian mobile number.');
      setLoading(false);
      return;
    }

    const formattedPhone = formatPhone(phone);

    try {
      if (isLogin) {
        await login(formattedPhone, password);
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        const result = await register(name, email, formattedPhone, password, role);
        if (result.status === 'pending') {
          toast.success(result.message || 'Admin account request submitted for approval.');
          setIsLogin(true);
        } else {
          toast.success('Account created successfully!');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'An error occurred';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (forgotStep === 'phone') {
      if (!validatePhone(forgotPhone)) {
        toast.error('Invalid phone number');
        return;
      }
      setLoading(true);
      try {
        const response = await api.post('/auth/forgot-password', { phone: formatPhone(forgotPhone) });
        toast.success('OTP sent to your phone number');
        setForgotStep('otp');
        if (response.data.otp) {
          setTimeout(() => { toast.info(`Demo OTP: ${response.data.otp}`, { duration: 10000 }); }, 1000);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send OTP');
      } finally { setLoading(false); }
    } else if (forgotStep === 'otp') {
      setLoading(true);
      try {
        await api.post('/auth/verify-otp', null, { params: { phone: formatPhone(forgotPhone), otp } });
        toast.success('OTP verified');
        setForgotStep('newPassword');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Invalid OTP');
      } finally { setLoading(false); }
    } else if (forgotStep === 'newPassword') {
      if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
      setLoading(true);
      try {
        await api.post('/auth/reset-password', { phone: formatPhone(forgotPhone), otp, new_password: newPassword });
        toast.success('Password reset successfully!');
        setShowForgotPassword(false);
        setForgotStep('phone');
        setForgotPhone('');
        setOtp('');
        setNewPassword('');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reset password');
      } finally { setLoading(false); }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--surface-bg)' }}>
      <div className="max-w-[420px] w-full animate-page-enter">
        <div className="bg-white rounded-2xl shadow-xl shadow-black/[0.03] border p-8 sm:p-10" style={{ borderColor: 'var(--surface-border)' }}>
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Aavaas" className="w-16 h-16 rounded-xl object-contain" />
          </div>

          <h1 className="text-2xl font-bold text-center text-slate-900 tracking-tight mb-1">
            Aavaas CRM
          </h1>
          <p className="text-center text-sm text-slate-500 mb-8">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</Label>
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required={!isLogin} className="mt-1.5 h-10 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required={!isLogin} className="mt-1.5 h-10 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="phone" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone Number</Label>
              <div className="relative mt-1.5">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-slate-500">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="text-sm font-medium">+91</span>
                </div>
                <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" required className="pl-[4.5rem] h-10 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400" maxLength={10} />
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">Enter 10-digit mobile number</p>
            </div>

            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="pr-10 h-10 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="role" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">I am a</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales_rep">Sales Rep</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                {role === 'admin' && <p className="text-[11px] text-slate-400 mt-2">Admin accounts require Super Admin approval</p>}
              </div>
            )}

            {isLogin && (
              <div className="flex justify-end">
                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs font-medium text-[#B89B72] hover:text-[#9A7F5A] transition-colors">
                  Forgot Password?
                </button>
              </div>
            )}

            <Button type="submit" className="w-full h-10 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm text-white" style={{ background: 'hsl(222, 47%, 11%)' }} disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Please wait...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? 'Sign In' : 'Sign Up'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span className="font-semibold text-slate-900">{isLogin ? 'Sign up' : 'Sign in'}</span>
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Demo: +919999999999 / superadmin123
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-lg">Reset Password</DialogTitle>
          </DialogHeader>
          
          {forgotStep === 'phone' && (
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="forgot-phone" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone Number</Label>
                <div className="relative mt-1.5">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-slate-500">
                    <Phone className="w-3.5 h-3.5" />
                    <span className="text-sm font-medium">+91</span>
                  </div>
                  <Input id="forgot-phone" type="tel" value={forgotPhone} onChange={(e) => setForgotPhone(e.target.value)} placeholder="9876543210" className="pl-[4.5rem] h-10 rounded-lg" maxLength={10} />
                </div>
              </div>
              <Button onClick={handleForgotPassword} disabled={loading} className="w-full h-10 rounded-lg">
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
            </div>
          )}

          {forgotStep === 'otp' && (
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="otp" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Enter OTP</Label>
                <Input id="otp" type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter 6-digit OTP" maxLength={6} className="mt-1.5 h-10 rounded-lg" />
                <p className="text-[11px] text-slate-400 mt-1.5">OTP sent to +91{forgotPhone}</p>
              </div>
              <Button onClick={handleForgotPassword} disabled={loading} className="w-full h-10 rounded-lg">
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </div>
          )}

          {forgotStep === 'newPassword' && (
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="new-password" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">New Password</Label>
                <div className="relative mt-1.5">
                  <Input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="pr-10 h-10 rounded-lg" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button onClick={handleForgotPassword} disabled={loading} className="w-full h-10 rounded-lg">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
